import {
    UPLOAD_PROTOCOL,
    UPLOAD_WINDOW_URL,
    uploadChannelName,
    uploadContext,
    uploadWindowName,
    createUploadId,
    ACTIVE_UPLOAD_STATES,
} from './protocol';

let channel;
let uploadWindow;
let navigationId = createUploadId();
let state = { items: [], openItems: [], updatedAt: 0, pending: false, connected: false };
const listeners = new Set();
const submissions = new Map();
const uploadWindows = new Map();

export function invalidateUploadNavigation() {
    navigationId = createUploadId();
}

export function isCurrentUploadNavigation(item) {
    return item.navigationId === navigationId;
}

function notify() {
    listeners.forEach(listener => listener(state));
}

function initialize() {
    if (channel || !uploadContext()?.userId) {
        return;
    }

    channel = new BroadcastChannel(uploadChannelName());
    channel.onmessage = ({ data }) => {
        if (data?.type !== 'status' || data.userId !== uploadContext().userId) {
            return;
        }

        uploadWindows.set(data.windowId || 'default', {
            items: data.items,
            updatedAt: Date.now(),
            closed: data.closed,
        });

        const snapshots = Array.from(uploadWindows.values());
        const openWindows = snapshots.filter(snapshot => !snapshot.closed);
        const activeWindows = openWindows.filter(snapshot => snapshot.items.some(item =>
            ACTIVE_UPLOAD_STATES.includes(item.status) || item.status === 'duplicate'));

        state = {
            ...state,
            items: snapshots.flatMap(snapshot => snapshot.items),
            openItems: openWindows.flatMap(snapshot => snapshot.items),
            updatedAt: Math.min(Date.now(), ...activeWindows.map(snapshot => snapshot.updatedAt)),
            connected: activeWindows.length > 0,
        };
        notify();
    };
    channel.postMessage({ type: 'status-request' });

    window.addEventListener('message', event => {
        const data = event.data;

        if (event.origin !== window.location.origin || event.source !== uploadWindow
            || data?.protocol !== UPLOAD_PROTOCOL || data.userId !== uploadContext().userId) {
            return;
        }

        const submission = submissions.get(data.requestId);

        if (!submission || data.type !== 'accepted') {
            return;
        }

        clearInterval(submission.interval);
        clearTimeout(submission.timeout);
        submissions.delete(data.requestId);
        state = { ...state, pending: submissions.size > 0 };
        submission.resolve(data.count);
        window.focus();
        notify();
    });

    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            channel.postMessage({ type: 'status-request' });
        }
    });

    window.addEventListener('beforeunload', event => {
        if (submissions.size) {
            event.preventDefault();
            event.returnValue = '';
        }
    });
}

export function subscribeUploads(listener) {
    initialize();
    listeners.add(listener);
    listener(state);

    return () => listeners.delete(listener);
}

export function requestUploadStatus() {
    if (!document.hidden) {
        channel?.postMessage({ type: 'status-request' });
    }
}

export function openUploadWindow() {
    initialize();

    // An empty URL reuses the named window without reloading its active uploads.
    uploadWindow = window.open(
        '',
        uploadWindowName(),
        'popup=yes,width=640,height=720,resizable=yes,scrollbars=yes',
    );

    if (!uploadWindow) {
        return false;
    }

    if (uploadWindow.location.href === 'about:blank') {
        uploadWindow.location.replace(UPLOAD_WINDOW_URL);
    }

    uploadWindow.focus();

    return true;
}

function sendSubmission(submission) {
    clearInterval(submission.interval);
    clearTimeout(submission.timeout);

    const send = () => {
        if (uploadWindow && !uploadWindow.closed) {
            uploadWindow.postMessage(submission.message, window.location.origin);
        }
    };

    send();
    submission.interval = setInterval(send, 500);
    submission.timeout = setTimeout(() => {
        clearInterval(submission.interval);
        submission.reject(new Error('The upload window did not respond. Open it and try again.'));
    }, 30000);
}

export function retryPendingUploads() {
    if (!openUploadWindow()) {
        return false;
    }

    submissions.forEach(sendSubmission);

    return true;
}

export function enqueueBackgroundUploads({ files, folderId, replace = 0 }) {
    initialize();

    const selectedFiles = Array.from(files);

    if (!selectedFiles.length) {
        return Promise.resolve(0);
    }

    let submission = Array.from(submissions.values()).find(item =>
        item.message.folderId === folderId && item.message.replace === replace
        && item.message.files.length === selectedFiles.length
        && item.message.files.every((file, index) => file === selectedFiles[index]));

    if (!submission) {
        const requestId = createUploadId();
        submission = {
            message: {
                protocol: UPLOAD_PROTOCOL,
                type: 'enqueue',
                requestId,
                navigationId,
                userId: uploadContext().userId,
                companyId: uploadContext().companyId,
                folderId,
                replace,
                files: selectedFiles,
                // File's custom properties are not preserved by structured clone.
                replaceNames: selectedFiles.map(file => file.replace_name || ''),
            },
        };
        submissions.set(requestId, submission);
    }

    state = { ...state, pending: true };
    notify();

    return new Promise((resolve, reject) => {
        submission.resolve = resolve;
        submission.reject = reject;

        if (!openUploadWindow()) {
            reject(new Error('Allow popups for It’sRapid, then open the upload window to continue.'));
            return;
        }

        sendSubmission(submission);
    });
}
